import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, AlertTriangle, RotateCcw } from 'lucide-react';
import { adminAPI } from '../../../api/bookings';
import { formatBookingReference } from '../../../utils/bookingUtils';

/**
 * CancelBookingDialog
 *
 * Replaces the old `window.confirm('cancel?')` with a real modal that:
 *  - Shows what's being cancelled (ref + date + time)
 *  - For confirmed Pocket Pay bookings, runs an eligibility check and
 *    surfaces a default-CHECKED "Also refund customer via Pocket Pay void"
 *    box. Admin can untick to keep funds at the gateway (rare, requires
 *    a written reason).
 *  - Optionally captures a void reason.
 *
 * Props:
 *  - booking: the booking being cancelled (or null when closed)
 *  - onClose(): close without action
 *  - onCancelled(): call after successful cancel; parent reloads list
 */
export default function CancelBookingDialog({ booking, onClose, onCancelled }) {
  const [eligibility, setEligibility] = useState(null);
  const [voidPayment, setVoidPayment] = useState(true);  // default-on per spec
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [eligLoading, setEligLoading] = useState(false);

  const isPocketPayConfirmed =
    booking
    && (booking.payment_method || '').toLowerCase().startsWith('pocket_pay')
    && booking.status === 'CONFIRMED';

  // Only need to hit the eligibility endpoint for confirmed pocket-pay bookings;
  // everything else has no void path and the dialog stays simple.
  useEffect(() => {
    let cancelled = false;
    if (!booking || !isPocketPayConfirmed) {
      setEligibility(null);
      setVoidPayment(false);  // not eligible → checkbox off & hidden
      return undefined;
    }
    setEligLoading(true);
    adminAPI.checkVoidEligibility(booking.id)
      .then((r) => { if (!cancelled) setEligibility(r); })
      .catch(() => { if (!cancelled) setEligibility({ eligible: false, reason: 'Could not check eligibility.' }); })
      .finally(() => { if (!cancelled) setEligLoading(false); });
    return () => { cancelled = true; };
  }, [booking, isPocketPayConfirmed]);

  // Reset state whenever the dialog opens for a new booking
  useEffect(() => {
    if (booking) {
      setVoidPayment(true);
      setReason('');
    }
  }, [booking?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = async () => {
    if (!booking) return;
    setSubmitting(true);
    try {
      const shouldVoid = isPocketPayConfirmed && eligibility?.eligible && voidPayment;
      // `reason` doubles as both the void-reason (when admin unticks void
      // on an eligible booking) AND the cancellation-reason persisted on
      // the booking + included in the customer email. Send whichever is
      // populated.
      const reasonForApi = reason.trim() || null;
      await adminAPI.cancelBooking(booking.id, { voidPayment: shouldVoid, reason: reasonForApi });
      onCancelled(shouldVoid);
    } catch (e) {
      // Bubble back to parent (it shows the toast). Keep dialog open so
      // admin can read the error inline.
      const msg = e?.response?.data?.detail || 'Failed to cancel booking';
      onCancelled(false, msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="cancel-booking-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Cancel Booking
          </DialogTitle>
          <DialogDescription>
            This releases the court slot and notifies the customer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Booking summary */}
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
            <div className="font-mono text-xs text-muted-foreground">
              {formatBookingReference(booking.id)}
            </div>
            <div className="font-medium mt-1">
              {booking.booking_date} · {booking.time_slot}
            </div>
            {(booking.customer_info?.user_name || booking.customer_info?.guest_name) && (
              <div className="text-xs text-muted-foreground mt-1">
                {booking.customer_info.user_name || booking.customer_info.guest_name}
              </div>
            )}
          </div>

          {/* Pocket Pay auto-void section (only for eligible bookings) */}
          {isPocketPayConfirmed && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 space-y-2">
              <div className="flex items-start gap-2">
                <RotateCcw className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                <div className="flex-1 text-sm">
                  <div className="font-semibold text-emerald-900">Pocket Pay refund</div>
                  {eligLoading ? (
                    <div className="text-xs text-emerald-800 mt-1">
                      <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
                      Checking refund eligibility…
                    </div>
                  ) : eligibility?.eligible ? (
                    <>
                      <p className="text-xs text-emerald-800 mt-1">
                        This payment can be voided (same-day). The customer's card / QR / biller will be reversed.
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Checkbox
                          id="void-checkbox"
                          checked={voidPayment}
                          onCheckedChange={(v) => setVoidPayment(!!v)}
                          data-testid="cancel-dialog-void-checkbox"
                        />
                        <Label htmlFor="void-checkbox" className="text-sm font-medium cursor-pointer">
                          Also refund customer via Pocket Pay void
                        </Label>
                      </div>
                      {!voidPayment && (
                        <div className="mt-2">
                          <Label htmlFor="void-reason" className="text-xs text-amber-900">
                            Why keep funds at the gateway? <span className="text-red-500">*</span>
                          </Label>
                          <Textarea
                            id="void-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Required written reason for the audit log"
                            className="mt-1 text-xs"
                            rows={2}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-emerald-800 mt-1">
                      {eligibility?.reason || 'This payment cannot be voided (settlement window passed). Booking will be cancelled without refund.'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Optional cancellation reason (always shown EXCEPT when the
              auto-void path is offering its own reason input above; reusing
              the same `reason` state keeps the form simple). */}
          {!(isPocketPayConfirmed && eligibility?.eligible && !voidPayment) && (
            <div className="space-y-1">
              <Label htmlFor="cancel-reason" className="text-sm">
                Reason <span className="text-muted-foreground font-normal">(optional, sent to customer)</span>
              </Label>
              <Textarea
                id="cancel-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 280))}
                placeholder="Short note about why this booking is being cancelled"
                className="text-xs"
                rows={2}
                data-testid="admin-cancel-reason"
              />
              <div className="text-[10px] text-muted-foreground text-right">{reason.length}/280</div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            data-testid="cancel-dialog-back-btn"
          >
            Keep Booking
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={
              submitting
              || eligLoading
              // If admin unticked the void on an eligible booking, require a reason
              || (isPocketPayConfirmed && eligibility?.eligible && !voidPayment && reason.trim().length < 4)
            }
            data-testid="cancel-dialog-confirm-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cancelling…</>
            ) : (
              <>
                {isPocketPayConfirmed && eligibility?.eligible && voidPayment
                  ? 'Cancel & Refund'
                  : 'Cancel Booking'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
